// « Mise en route » — /onboarding (Backend docs/onboarding.md). The steps a new
// account goes through, chosen by what it confirms it is (logistician or
// e-merchant). Each step's state comes from the account's data (GET
// /api/setup/onboarding): a step is done because the carrier, the client, the
// channel or the source exists. Simple things are done right here (profile,
// carriers from the catalogue, B2B clients); the rest opens the page that sets
// it up and comes back. ?step=<key> opens a step (copilot, sidebar, links).

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaArrowRight } from 'react-icons/fa';
import { StepPanel, STEP_TEXT } from './OnboardingSteps';
import { notifyOnboardingChanged } from './events';
import './Onboarding.css';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

const STATUS_TEXT = { done: 'Fait', pending: 'À faire', skipped: 'Plus tard' };

const Onboarding = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [status, setStatus] = useState(null);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(() => searchParams.get('step'));
    const [busy, setBusy] = useState(false);
    const [confirmDismiss, setConfirmDismiss] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await axios.get('/api/setup/onboarding', authHeaders());
            setStatus(res.data);
            setError('');
        } catch (e) {
            setError(t('onboarding.loadError', 'La mise en route n’a pas pu être chargée.'));
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);
    // Coming back from a settings page (OAuth, a source, a carrier) refreshes the facts.
    useEffect(() => {
        const onFocus = () => load();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [load]);

    const steps = status?.steps || [];
    const known = steps.find((s) => s.key === selected);
    const current = known || steps.find((s) => s.key === status?.current_step) || steps[0];
    const index = current ? steps.indexOf(current) : -1;

    const select = (key) => {
        setSelected(key);
        const next = new URLSearchParams(searchParams);
        next.set('step', key);
        setSearchParams(next, { replace: true });
    };

    // Every write returns the fresh status: the page never guesses.
    const run = async (request) => {
        setBusy(true);
        setError('');
        try {
            const res = await request();
            if (res?.data?.steps) setStatus(res.data);
            notifyOnboardingChanged();
            return res;
        } catch (e) {
            const detail = e.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : t('onboarding.saveError', 'Le changement n’a pas pu être enregistré.'));
            return null;
        } finally {
            setBusy(false);
        }
    };

    const actions = {
        setProfile: (profile) => run(() => axios.put('/api/setup/onboarding/profile', { profile }, authHeaders())),
        decide: (step, decision, mode) => run(() => axios.post(`/api/setup/onboarding/steps/${step}`, { decision, ...(mode ? { mode } : {}) }, authHeaders())),
        refresh: load,
        busy,
    };

    const goNext = () => {
        const next = steps.slice(index + 1).find((s) => s.status !== 'done') || steps[index + 1];
        if (next) select(next.key);
    };

    const finish = async () => {
        const res = await run(() => axios.post('/api/setup/onboarding/finish', {}, authHeaders()));
        if (res) navigate('/dashboard');
    };

    const dismiss = async () => {
        const res = await run(() => axios.post('/api/setup/onboarding/dismiss', {}, authHeaders()));
        if (res) navigate('/dashboard');
    };

    if (!status) {
        return <div className="ob-page"><p className="ob-muted">{error || t('common.loading', 'Chargement…')}</p></div>;
    }

    const percent = status.total ? Math.round((status.done / status.total) * 100) : 0;
    return (
        <div className="ob-page">
            <header className="ob-head">
                <div className="ob-head-text">
                    <h1>{t('onboarding.title', 'Mise en route')}</h1>
                    <p>{t('onboarding.subtitle', 'Quelques étapes pour que chaque ticket soit traité de bout en bout. Remettez-en à plus tard si besoin : cette page reste là.')}</p>
                </div>
                <div className="ob-progress" aria-label={t('onboarding.progressLabel', 'Étapes faites')}>
                    <strong>{status.done}/{status.total}</strong>
                    <span>{t('onboarding.progress', 'étapes faites')}</span>
                    <span className="ob-meter" aria-hidden="true"><span style={{ width: `${percent}%` }} /></span>
                </div>
            </header>

            {status.completed && (
                <div className="ob-banner ob-banner--done" role="status">
                    <FaCheck aria-hidden="true" />
                    <span>{status.dismissed
                        ? t('onboarding.dismissedBanner', 'Mise en route masquée. Les étapes restent consultables ici.')
                        : t('onboarding.completedBanner', 'Mise en route terminée. Vous pouvez revenir sur chaque étape quand vous voulez.')}</span>
                </div>
            )}
            {!status.completed && status.can_finish && (
                <div className="ob-banner ob-banner--ready">
                    <span>{t('onboarding.readyBanner', 'Tout est prêt : vos tickets peuvent être traités de bout en bout.')}</span>
                    <button type="button" className="u-btn u-btn--primary" disabled={busy} onClick={finish}>
                        {t('onboarding.finish', 'Terminer la mise en route')}
                    </button>
                </div>
            )}
            {error && <p className="ob-error" role="alert">{error}</p>}

            <div className="ob-body">
                <nav className="ob-steps" aria-label={t('onboarding.stepsLabel', 'Étapes')}>
                    <ol>
                        {steps.map((step, i) => (
                            <li key={step.key}>
                                <button type="button" className={`ob-step ob-step--${step.status} ${step.key === current?.key ? 'ob-step--current' : ''}`}
                                    aria-current={step.key === current?.key ? 'step' : undefined} onClick={() => select(step.key)}>
                                    <span className="ob-step-mark" aria-hidden="true">{step.status === 'done' ? <FaCheck /> : i + 1}</span>
                                    <span className="ob-step-text">
                                        <span className="ob-step-name">{t(`onboarding.steps.${step.key}.title`, STEP_TEXT[step.key]?.[0] || step.key)}</span>
                                        <span className="ob-step-status">{t(`onboarding.status.${step.status}`, STATUS_TEXT[step.status])}</span>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ol>
                </nav>

                {current && (
                    <section className="ob-panel" aria-labelledby="ob-panel-title">
                        <div className="ob-panel-head">
                            <span className="ob-panel-index">{t('onboarding.stepOf', 'Étape {{n}} sur {{total}}', { n: index + 1, total: steps.length })}</span>
                            <h2 id="ob-panel-title">{t(`onboarding.steps.${current.key}.title`, STEP_TEXT[current.key]?.[0] || current.key)}</h2>
                            <p>{t(`onboarding.steps.${current.key}.why`, STEP_TEXT[current.key]?.[1] || '')}</p>
                        </div>

                        <StepPanel step={current} status={status} actions={actions} />

                        <footer className="ob-panel-foot">
                            <div className="ob-panel-foot-left">
                                {current.status === 'pending' && current.accepts.includes('skipped') && (
                                    <button type="button" className="ob-link" disabled={busy} onClick={async () => { await actions.decide(current.key, 'skipped'); goNext(); }}>
                                        {t('onboarding.later', 'Plus tard')}
                                    </button>
                                )}
                                {current.decision && (
                                    <button type="button" className="ob-link" disabled={busy} onClick={() => actions.decide(current.key, 'reopen')}>
                                        {t('onboarding.reopen', 'Revenir sur ce choix')}
                                    </button>
                                )}
                            </div>
                            {index < steps.length - 1 && (
                                <button type="button" className="u-btn u-btn--secondary" onClick={goNext}>
                                    {t('onboarding.next', 'Étape suivante')} <FaArrowRight aria-hidden="true" />
                                </button>
                            )}
                        </footer>
                    </section>
                )}
            </div>

            {!status.completed && status.started && (
                <div className="ob-dismiss">
                    {confirmDismiss ? (
                        <>
                            <span>{t('onboarding.dismissConfirm', 'Masquer la mise en route ? Le ticket de démonstration sera supprimé.')}</span>
                            <button type="button" className="ob-link" disabled={busy} onClick={dismiss}>{t('onboarding.dismissYes', 'Masquer')}</button>
                            <button type="button" className="ob-link" onClick={() => setConfirmDismiss(false)}>{t('common.cancel', 'Annuler')}</button>
                        </>
                    ) : (
                        <button type="button" className="ob-link ob-link--muted" onClick={() => setConfirmDismiss(true)}>
                            {t('onboarding.dismiss', 'Masquer la mise en route')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default Onboarding;
